import { createSlice } from '@reduxjs/toolkit';
import { batch } from 'react-redux';
import { canDisplayContestNews } from 'src/capabilities';
import axios from 'src/utils/axios';

const isAbsent = x => x === undefined || x === null;

const initialState = {
  contestNews: [],
  trimpoengNews: [],
  isFetchingContest: false,
  isFetchingTrimpoeng: false,
  fetched: false,
  selectedLocation: null,
  noMoreContestNews: false,
  noMoreTrimpoengNews: false,
  unreadCount: 0,
  unreadContestCount: 0,
  unreadTrimpoengCount: 0,
  readContestNewsIds: [],
  readTrimpoengNewsIds: []
};

const slice = createSlice({
  name: 'news',
  initialState,
  reducers: {
    addNews(state, action) {
      const { news, isGlobal } = action.payload;
      if (isGlobal) {
        state.trimpoengNews.unshift(news);
      } else {
        state.contestNews.unshift(news);
      }
    },
    updateNews(state, action) {
      const { news, isGlobal } = action.payload;
      if (isGlobal) {
        state.trimpoengNews = state.trimpoengNews.map(item =>
          item.id === news.id ? news : item
        );
      } else {
        state.contestNews = state.contestNews.map(item =>
          item.id === news.id ? news : item
        );
      }
    },
    deleteNews(state, action) {
      const { contest_id: contestId, id } = action.payload;
      if (isAbsent(contestId)) {
        state.trimpoengNews = state.trimpoengNews.filter(
          post => post.id !== id
        );
      } else {
        state.contestNews = state.contestNews.filter(post => post.id !== id);
      }
    },
    setContestNews(state, action) {
      const { contestId, news } = action.payload;
      if (isAbsent(contestId)) {
        state.trimpoengNews = news;
      } else {
        state.contestNews = news;
      }
    },
    appendContestNews(state, action) {
      const { contestId, news } = action.payload;
      if (isAbsent(contestId)) {
        state.trimpoengNews.push(...news);
      } else {
        state.contestNews.push(...news);
      }
    },
    setFetchingContest(state, action) {
      const { contestId, value } = action.payload;
      if (isAbsent(contestId)) {
        state.isFetchingTrimpoeng = value;
      } else {
        state.isFetchingContest = value;
      }
    },
    setFetched(state, action) {
      state.fetched = action.payload;
    },
    setNoMoreContestNews(state, action) {
      const { contestId, value } = action.payload;
      if (isAbsent(contestId)) {
        state.noMoreTrimpoengNews = value;
      } else {
        state.noMoreContestNews = value;
      }
    },
    setUnreadCount(state, action) {
      state.unreadCount = action.payload;
    },
    setUnreadContestCount(state, action) {
      state.unreadContestCount = action.payload;
    },
    setUnreadTrimpoengCount(state, action) {
      state.unreadTrimpoengCount = action.payload;
    },
    addReadContestNewsIds(state, action) {
      const newIds = action.payload;
      state.readContestNewsIds = [
        ...new Set([...state.readContestNewsIds, ...newIds])
      ];
    },
    addReadTrimpoengNewsIds(state, action) {
      const newIds = action.payload;
      state.readTrimpoengNewsIds = [
        ...new Set([...state.readTrimpoengNewsIds, ...newIds])
      ];
    },
    setReadContestNewsIds(state, action) {
      state.readContestNewsIds = action.payload;
    },
    setReadTrimpoengNewsIds(state, action) {
      state.readTrimpoengNewsIds = action.payload;
    }
  }
});

export const { reducer } = slice;

// fetch news for contestId, or for Trimpoeng if contestId is not set
export const getContestNews = ({
  offset,
  limit,
  contestId
}) => async dispatch => {
  try {
    dispatch(slice.actions.setFetchingContest({ contestId, value: true }));

    const params = new URLSearchParams();
    if (!isAbsent(offset)) {
      params.append('offset', offset);
    }
    if (!isAbsent(limit)) {
      params.append('limit', limit);
    }
    if (!isAbsent(contestId)) {
      params.append('contest_id', contestId);
    }
    const response = await axios.get(`/news?${params}`);

    const news = response.data.data;

    batch(() => {
      if (isAbsent(offset) || offset === 0) {
        dispatch(slice.actions.setContestNews({ contestId, news }));
      } else {
        dispatch(slice.actions.appendContestNews({ contestId, news }));
      }
      dispatch(
        slice.actions.setNoMoreContestNews({
          contestId,
          value: news.length < (limit ?? 1)
        })
      );
      dispatch(slice.actions.setFetchingContest({ contestId, value: false }));
    });
  } catch (error) {
    console.error('Error fetching news:', error);
    dispatch(slice.actions.setFetchingContest({ contestId, value: false }));
  }
};

export const initAllNews = ({ limit, contest }) => async dispatch => {
  batch(() => {
    dispatch(getContestNews({ offset: 0, limit, contestId: null }));
    if (contest && canDisplayContestNews(contest)) {
      dispatch(getContestNews({ offset: 0, limit, contestId: contest.id }));
    }
  });
};

export const updateNews = (
  news,
  selectedContest,
  isGlobal
) => async dispatch => {
  try {
    // Add contest_id to the news object
    const updatedNews = {
      ...news,
      contest_id: isGlobal ? null : selectedContest?.id || null
    };

    const response = await axios.put(`/news/${news.id}`, { news: updatedNews });

    if (response.status !== 200) {
      return response.data.message;
    }

    dispatch(slice.actions.updateNews({ news: response.data, isGlobal }));

    return true;
  } catch (e) {
    return e.message;
  }
};

export const addNews = (news, selectedContest, isGlobal) => async dispatch => {
  try {
    const response = await axios.post('/news', {
      news: {
        ...news,
        contest_id: isGlobal ? null : selectedContest?.id || null
      }
    });

    if (response.status !== 201) {
      return response.data.message;
    }

    dispatch(slice.actions.addNews({ news: response.data, isGlobal }));

    return true;
  } catch (e) {
    return e.message;
  }
};

export const deleteNews = news => async dispatch => {
  try {
    const response = await axios.delete(`/news/${news.id}`);

    if (response.status !== 204) {
      return response.data.message;
    }

    dispatch(slice.actions.deleteNews(news));

    return true;
  } catch (e) {
    return e.message;
  }
};

export const setUnreadCount = count => async dispatch => {
  dispatch(slice.actions.setUnreadCount(count));
};

export const setUnreadContestCount = count => async dispatch => {
  dispatch(slice.actions.setUnreadContestCount(count));
};

export const setUnreadTrimpoengCount = count => async dispatch => {
  dispatch(slice.actions.setUnreadTrimpoengCount(count));
};

export const addReadContestNewsIds = ids => async dispatch => {
  dispatch(slice.actions.addReadContestNewsIds(ids));
};

export const addReadTrimpoengNewsIds = ids => async dispatch => {
  dispatch(slice.actions.addReadTrimpoengNewsIds(ids));
};

export const setReadContestNewsIds = ids => async dispatch => {
  dispatch(slice.actions.setReadContestNewsIds(ids));
};

export const setReadTrimpoengNewsIds = ids => async dispatch => {
  dispatch(slice.actions.setReadTrimpoengNewsIds(ids));
};

export default slice;
